import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all roles
 */
export const fetchRoles = createAsyncThunk(
  'roles/fetchRoles',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Roles.app_roles_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch roles');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roles');
    }
  }
);

/**
 * Fetch a single role by ID
 */
export const fetchRoleById = createAsyncThunk(
  'roles/fetchRoleById',
  async ({ token, roleId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Roles.app_roles_get,
        roleId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch role');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch role');
    }
  }
);

/**
 * Create a new role
 */
export const createRole = createAsyncThunk(
  'roles/createRole',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      data.append('name', formData.name);

      const response = await EntityService.addEntity(
        token,
        API.Roles.app_roles_add,
        data
      );
      if (response.data.status === 201 || response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create role');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create role';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a role
 */
export const deleteRole = createAsyncThunk(
  'roles/deleteRole',
  async ({ token, roleId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Roles.app_roles_delete,
        roleId
      );
      if (response.data.status === 200) {
        return roleId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete role');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete role');
    }
  }
);

const initialState = {
  // List state
  entities: [],
  
  // Selected entity state
  selectedEntity: null,
  initialFormData: null,
  
  // Status states
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  deleteStatus: 'idle',
  
  // Error states
  listError: null,
  detailError: null,
  createError: null,
  deleteError: null,
  
  // Form validation errors
  formErrors: {},
};

const roleSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
    },
    
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.createError = null;
      state.formErrors = {};
    },
    
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch roles list
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.listError = null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      });

    // Fetch role by ID
    builder
      .addCase(fetchRoleById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      });

    // Create role
    builder
      .addCase(createRole.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createRole.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create role';
        state.formErrors = errorData.errors || {};
      });

    // Delete role
    builder
      .addCase(deleteRole.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(role => role.uuid !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = roleSlice.actions;

export const selectRoles = (state) => state.roles.entities;
export const selectSelectedRole = (state) => state.roles.selectedEntity;
export const selectInitialFormData = (state) => state.roles.initialFormData;
export const selectRolesListStatus = (state) => state.roles.listStatus;
export const selectRoleDetailStatus = (state) => state.roles.detailStatus;
export const selectRoleCreateStatus = (state) => state.roles.createStatus;
export const selectRoleDeleteStatus = (state) => state.roles.deleteStatus;
export const selectRolesListError = (state) => state.roles.listError;
export const selectRoleDetailError = (state) => state.roles.detailError;
export const selectRoleCreateError = (state) => state.roles.createError;
export const selectRoleDeleteError = (state) => state.roles.deleteError;
export const selectRoleFormErrors = (state) => state.roles.formErrors;

export default roleSlice.reducer;
